/**
 * Plan Mode Store
 *
 * Zustand store for Plan Mode pipeline state management.
 * Manages the full lifecycle: conversation → research → roadmap → phases → execution → audit.
 * Persisted to sessionStorage so state survives refresh but clears on tab close.
 *
 * Loading flags are in the store (not persisted) so multiple UI components can subscribe.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { persist, createJSONStorage } from 'zustand/middleware';
import { TabLockSchema } from '@/schemas/storage';
import type {
  PlanPipelineMode,
  PlanPipelineStage,
  PlanResearchContext,
  Roadmap,
  RoadmapPhase,
  AuditResult,
  PhasePrompt,
  QualityGateBlock,
  Plan,
  PlanSectionStatus,
  PlanSectionDetails,
  SavedRoadmapSummary,
  ProposedSkill,
  ProposedAgent,
  ProposedKnowledgeBase,
  ProposedWorkflow,
  ProposedSchema,
  ProposedPageDesign,
} from '@/types/plan';
import { PIPELINE_STAGE_LABELS } from '@/types/plan';
import { planApi } from '@/services/api/agentApi';

// ---------------------------------------------------------------------------
// Module-level variables (not proxied by immer, not persisted)
// ---------------------------------------------------------------------------

const TAB_ID =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `tab_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const LOCK_KEY = 'terramatrix:plan-mode-lock';
const LOCK_HEARTBEAT_MS = 10_000;
const LOCK_STALE_MS = 30_000;
let heartbeatInterval: ReturnType<typeof setInterval> | null = null;

let activeAbortController: AbortController | null = null;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PlanConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface PlanStoreState {
  // Core
  isActive: boolean;
  pipelineMode: PlanPipelineMode;
  pipelineStage: PlanPipelineStage;
  pipelineProgress: number;
  planName: string | null;

  // Conversation
  conversationHistory: PlanConversationMessage[];
  currentThreadId: string | null;
  isConversingLoading: boolean;

  // Plan (Quick Mode result)
  plan: Plan | null;
  planReady: boolean;
  isCompiling: boolean;

  // Research (Full Mode)
  researchContext: PlanResearchContext | null;
  isResearching: boolean;
  researchProgress: {
    currentTopic: string;
    topicIndex: number;
    totalTopics: number;
    urlsScraped: number;
  } | null;

  // Roadmap (Full Mode)
  roadmap: Roadmap | null;
  isGeneratingRoadmap: boolean;
  roadmapAudit: AuditResult | null;
  isAuditingRoadmap: boolean;

  // Phase Decomposition (Full Mode)
  phases: PhasePrompt[];
  phaseAudits: Record<string, AuditResult>;
  editedPhasePrompts: Record<string, string>;
  isDecomposing: boolean;
  isAuditingPhases: boolean;

  // Execution
  isExecuting: boolean;
  currentExecutingPhaseId: string | null;
  executionErrors: Record<string, string>;
  implementationAudits: Record<string, AuditResult>;

  // Quality Gate
  activeQualityGate: QualityGateBlock | null;
  qualityGateResolving: boolean;

  // Final
  finalAudit: AuditResult | null;

  // Discovered context
  discoveredSchemas: string[];

  // Error
  error: string | null;

  // Plan History (populated by orchestration hook, consumed by PlanModeToggle)
  planHistory: Array<{ id: string; title: string; date: string; auditScore?: number }>;

  // Saved Roadmaps (persisted to backend MongoDB)
  savedRoadmaps: SavedRoadmapSummary[];
  savedRoadmapsLoaded: boolean;

  // Skill Wizard
  proposedSkill: ProposedSkill | null;
  skillReady: boolean;

  // Creative Studio Wizards
  proposedAgent: ProposedAgent | null;
  agentReady: boolean;
  proposedKnowledgeBase: ProposedKnowledgeBase | null;
  knowledgeReady: boolean;
  proposedWorkflow: ProposedWorkflow | null;
  workflowReady: boolean;
  proposedSchema: ProposedSchema | null;
  dataReady: boolean;
  proposedPageDesign: ProposedPageDesign | null;
  pageDesignReady: boolean;

  // Execution metrics (populated by orchestration hook, consumed by PlanCompletionCard)
  artifactCounts: Record<string, number> | null;
  totalLLMCalls: number;
  executionTimeMs: number;

  // Timing (transient — not persisted, used by PlanPipelineProgress for live timers)
  pipelineStartedAt: number | null;
  stageTimings: Record<string, { startedAt: number; endedAt: number | null }>;
  subStepTimings: Record<string, { startedAt: number; endedAt: number | null }>;
}

interface PlanStoreActions {
  // Activation & Mode
  activate: () => boolean;
  deactivate: () => void;
  toggle: () => boolean;
  setPipelineMode: (mode: PlanPipelineMode) => void;
  setPlanName: (name: string) => void;

  // Pipeline
  setPipelineStage: (stage: PlanPipelineStage) => void;
  setPipelineProgress: (progress: number) => void;

  // Conversation
  addUserMessage: (content: string) => void;
  addAssistantMessage: (content: string) => void;
  setConversingLoading: (loading: boolean) => void;
  setConversationHistory: (messages: PlanConversationMessage[]) => void;
  clearConversation: () => void;
  setThreadId: (threadId: string) => void;
  setPlanReady: (ready: boolean) => void;
  addDiscoveredSchemas: (schemas: string[]) => void;

  // Research
  setResearching: (researching: boolean) => void;
  setResearchContext: (ctx: PlanResearchContext) => void;
  setResearchProgress: (progress: PlanStoreState['researchProgress']) => void;

  // Roadmap
  setRoadmap: (roadmap: Roadmap | null) => void;
  updateRoadmapPhase: (phaseId: string, updates: Partial<RoadmapPhase>) => void;
  setGeneratingRoadmap: (generating: boolean) => void;
  setRoadmapAudit: (audit: AuditResult) => void;
  setAuditingRoadmap: (auditing: boolean) => void;

  // Phase Decomposition
  setPhases: (phases: PhasePrompt[]) => void;
  setDecomposing: (decomposing: boolean) => void;
  setPhaseAudit: (phaseId: string, audit: AuditResult) => void;
  setEditedPhasePrompt: (phaseId: string, prompt: string) => void;
  setAuditingPhases: (auditing: boolean) => void;

  // Plan (Quick Mode)
  setPlan: (plan: Plan | null) => void;
  setCompiling: (compiling: boolean) => void;
  updateSectionStatus: (sectionId: string, status: PlanSectionStatus) => void;
  updateSectionArtifacts: (sectionId: string, artifacts: Record<string, string[]>) => void;

  // Execution
  setExecuting: (executing: boolean) => void;
  setCurrentExecutingPhaseId: (phaseId: string | null) => void;
  setExecutionError: (phaseId: string, error: string) => void;
  setImplementationAudit: (phaseId: string, audit: AuditResult) => void;

  // Abort
  getAbortController: () => AbortController | null;
  setAbortController: (controller: AbortController | null) => void;
  abortExecution: () => void;

  // Final Audit
  setFinalAudit: (audit: AuditResult) => void;

  // Quality Gate
  setActiveQualityGate: (gate: QualityGateBlock | null) => void;
  resolveQualityGate: (action: 'proceed' | 'revise' | 'skip' | 'discard') => void;

  // Error
  setError: (error: string | null) => void;

  // Plan History
  addToPlanHistory: (entry: { id: string; title: string; date: string; auditScore?: number }) => void;

  // Saved Roadmaps
  saveCurrentRoadmap: () => Promise<void>;
  loadSavedRoadmaps: () => Promise<void>;
  restoreRoadmap: (id: string) => Promise<void>;
  deleteSavedRoadmap: (id: string) => Promise<void>;

  // Execution Metrics
  setArtifactCounts: (counts: Record<string, number>) => void;
  incrementLLMCalls: (count?: number) => void;
  setExecutionTimeMs: (ms: number) => void;
  undoAllPhases: () => void;

  // Skill Wizard
  setProposedSkill: (skill: ProposedSkill | null) => void;
  setSkillReady: (ready: boolean) => void;
  clearSkillWizard: () => void;

  // Creative Studio Wizards
  setProposedAgent: (agent: ProposedAgent | null) => void;
  setAgentReady: (ready: boolean) => void;
  setProposedKnowledgeBase: (kb: ProposedKnowledgeBase | null) => void;
  setKnowledgeReady: (ready: boolean) => void;
  setProposedWorkflow: (wf: ProposedWorkflow | null) => void;
  setWorkflowReady: (ready: boolean) => void;
  setProposedSchema: (schema: ProposedSchema | null) => void;
  setDataReady: (ready: boolean) => void;
  setProposedPageDesign: (design: ProposedPageDesign | null) => void;
  setPageDesignReady: (ready: boolean) => void;

  // Timing
  setPipelineStartedAt: (ts: number) => void;
  recordStageStart: (stage: string) => void;
  recordStageEnd: (stage: string) => void;
  recordSubStepStart: (phaseId: string, stepName: string) => void;
  recordSubStepEnd: (phaseId: string, stepName: string) => void;

  // Reset
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const INITIAL_STATE: PlanStoreState = {
  isActive: false,
  pipelineMode: 'full',
  pipelineStage: 'idle',
  pipelineProgress: 0,
  planName: null,
  conversationHistory: [],
  currentThreadId: null,
  isConversingLoading: false,
  plan: null,
  planReady: false,
  isCompiling: false,
  researchContext: null,
  isResearching: false,
  researchProgress: null,
  roadmap: null,
  isGeneratingRoadmap: false,
  roadmapAudit: null,
  isAuditingRoadmap: false,
  phases: [],
  phaseAudits: {},
  editedPhasePrompts: {},
  isDecomposing: false,
  isAuditingPhases: false,
  isExecuting: false,
  currentExecutingPhaseId: null,
  executionErrors: {},
  implementationAudits: {},
  activeQualityGate: null,
  qualityGateResolving: false,
  finalAudit: null,
  discoveredSchemas: [],
  error: null,
  planHistory: [],
  savedRoadmaps: [],
  savedRoadmapsLoaded: false,
  proposedSkill: null,
  skillReady: false,
  proposedAgent: null,
  agentReady: false,
  proposedKnowledgeBase: null,
  knowledgeReady: false,
  proposedWorkflow: null,
  workflowReady: false,
  proposedSchema: null,
  dataReady: false,
  proposedPageDesign: null,
  pageDesignReady: false,
  artifactCounts: null,
  totalLLMCalls: 0,
  executionTimeMs: 0,
  pipelineStartedAt: null,
  stageTimings: {},
  subStepTimings: {},
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const usePlanStore = create<PlanStoreState & PlanStoreActions>()(
  subscribeWithSelector(
    immer(
      persist(
        (set, get) => ({
          ...INITIAL_STATE,

          // ── Activation & Mode ──────────────────────────────────────

          activate: () => {
            try {
              const raw = localStorage.getItem(LOCK_KEY);
              if (raw) {
                const parsed: unknown = JSON.parse(raw);
                const lockResult = TabLockSchema.safeParse(parsed);
                if (lockResult.success) {
                  const lock = lockResult.data;
                  if (lock.tabId !== TAB_ID && Date.now() - lock.timestamp < LOCK_STALE_MS) {
                    return false;
                  }
                }
              }
            } catch { /* ignore parse errors */ }

            localStorage.setItem(LOCK_KEY, JSON.stringify({ tabId: TAB_ID, timestamp: Date.now() }));
            if (heartbeatInterval) clearInterval(heartbeatInterval);
            heartbeatInterval = setInterval(() => {
              localStorage.setItem(LOCK_KEY, JSON.stringify({ tabId: TAB_ID, timestamp: Date.now() }));
            }, LOCK_HEARTBEAT_MS);

            set((s) => {
              s.isActive = true;
              s.pipelineStage = 'conversing';
              s.error = null;
            });
            return true;
          },

          deactivate: () => {
            try {
              const raw = localStorage.getItem(LOCK_KEY);
              if (raw) {
                const parsed: unknown = JSON.parse(raw);
                const lockResult = TabLockSchema.safeParse(parsed);
                if (lockResult.success && lockResult.data.tabId === TAB_ID) {
                  localStorage.removeItem(LOCK_KEY);
                }
              }
            } catch { /* ignore */ }
            if (heartbeatInterval) {
              clearInterval(heartbeatInterval);
              heartbeatInterval = null;
            }
            set((s) => {
              s.isActive = false;
              // Reset all wizard state
              s.proposedSkill = null;
              s.skillReady = false;
              s.proposedAgent = null;
              s.agentReady = false;
              s.proposedKnowledgeBase = null;
              s.knowledgeReady = false;
              s.proposedWorkflow = null;
              s.workflowReady = false;
              s.proposedSchema = null;
              s.dataReady = false;
              s.proposedPageDesign = null;
              s.pageDesignReady = false;
            });
          },

          toggle: () => {
            if (get().isActive) {
              get().deactivate();
              return false;
            }
            return get().activate();
          },

          setPipelineMode: (mode) => set((s) => { s.pipelineMode = mode; }),
          setPlanName: (name) => set((s) => { s.planName = name; }),

          // ── Pipeline ───────────────────────────────────────────────

          setPipelineStage: (stage) => set((s) => { s.pipelineStage = stage; }),
          setPipelineProgress: (progress) => set((s) => {
            s.pipelineProgress = Math.max(0, Math.min(100, progress));
          }),

          // ── Conversation ───────────────────────────────────────────

          addUserMessage: (content) => set((s) => {
            s.conversationHistory.push({ role: 'user', content });
          }),

          addAssistantMessage: (content) => set((s) => {
            s.conversationHistory.push({ role: 'assistant', content });
          }),

          setConversingLoading: (loading) => set((s) => { s.isConversingLoading = loading; }),

          setConversationHistory: (messages) => set((s) => {
            s.conversationHistory = messages;
          }),

          clearConversation: () => set((s) => { s.conversationHistory = []; }),

          setThreadId: (threadId) => set((s) => { s.currentThreadId = threadId; }),

          setPlanReady: (ready) => set((s) => { s.planReady = ready; }),

          addDiscoveredSchemas: (schemas) => set((s) => {
            const existing = new Set(s.discoveredSchemas);
            for (const schema of schemas) {
              existing.add(schema);
            }
            s.discoveredSchemas = Array.from(existing);
          }),

          // ── Research ───────────────────────────────────────────────

          setResearching: (researching) => set((s) => { s.isResearching = researching; }),
          setResearchContext: (ctx) => set((s) => { s.researchContext = ctx; }),
          setResearchProgress: (progress) => set((s) => { s.researchProgress = progress; }),

          // ── Roadmap ────────────────────────────────────────────────

          setRoadmap: (roadmap) => set((s) => { s.roadmap = roadmap; }),
          updateRoadmapPhase: (phaseId, updates) => set((s) => {
            if (!s.roadmap) return;
            const idx = s.roadmap.phases.findIndex(p => p.id === phaseId);
            if (idx === -1) return;
            s.roadmap.phases[idx] = { ...s.roadmap.phases[idx], ...updates };
            s.roadmap.updatedAt = new Date().toISOString();
          }),
          setGeneratingRoadmap: (generating) => set((s) => { s.isGeneratingRoadmap = generating; }),
          setRoadmapAudit: (audit) => set((s) => { s.roadmapAudit = audit; }),
          setAuditingRoadmap: (auditing) => set((s) => { s.isAuditingRoadmap = auditing; }),

          // ── Phase Decomposition ────────────────────────────────────

          setPhases: (phases) => set((s) => { s.phases = phases; }),
          setDecomposing: (decomposing) => set((s) => { s.isDecomposing = decomposing; }),
          setPhaseAudit: (phaseId, audit) => set((s) => {
            s.phaseAudits[phaseId] = audit;
          }),
          setEditedPhasePrompt: (phaseId, prompt) => set((s) => {
            s.editedPhasePrompts[phaseId] = prompt;
          }),
          setAuditingPhases: (auditing) => set((s) => { s.isAuditingPhases = auditing; }),

          // ── Plan (Quick Mode) ──────────────────────────────────────

          setPlan: (plan) => set((s) => {
            // Normalize sections: backend may return Record<domain, obj> or stale sessionStorage may have it
            if (plan && plan.sections && !Array.isArray(plan.sections)) {
              plan = {
                ...plan,
                sections: Object.entries(plan.sections as Record<string, unknown>).map(([key, section]) => {
                  const s = section as Record<string, unknown>;
                  let domainKey = key;
                  let pageName: string | undefined;
                  if (key.startsWith('visual-editor:')) {
                    domainKey = 'visual-editor';
                    pageName = key.split(':')[1];
                  }
                  return {
                    id: `section-${key}`,
                    domain: domainKey as Plan['sections'][number]['domain'],
                    title: (s.title || key) as string,
                    description: (s.description || '') as string,
                    executionPrompt: (s.executionPrompt || '') as string,
                    details: (s.details || {}) as PlanSectionDetails,
                    dependsOn: ((s.dependsOn || []) as string[]).map((dep: string) =>
                      dep.startsWith('section-') ? dep : `section-${dep}`),
                    status: (s.status || 'pending') as PlanSectionStatus,
                    ...(pageName ? { pageName } : {}),
                  };
                }),
              };
            }
            s.plan = plan;
            // Clear stale edited prompts so freshly compiled executionPrompts are used.
            // editedPhasePrompts is sessionStorage-persisted and section IDs are deterministic
            // (e.g. "section-visual-editor:Home"), so a previous compilation's edits would
            // silently override the new plan's prompts.
            s.editedPhasePrompts = {};
          }),
          setCompiling: (compiling) => set((s) => { s.isCompiling = compiling; }),

          updateSectionStatus: (sectionId, status) => set((s) => {
            const section = s.plan?.sections.find((sec) => sec.id === sectionId);
            if (section) section.status = status;
          }),

          updateSectionArtifacts: (sectionId, artifacts) => set((s) => {
            const section = s.plan?.sections.find((sec) => sec.id === sectionId);
            if (section) section.artifacts = artifacts;
          }),

          // ── Execution ──────────────────────────────────────────────

          setExecuting: (executing) => set((s) => { s.isExecuting = executing; }),

          setCurrentExecutingPhaseId: (phaseId) => set((s) => {
            s.currentExecutingPhaseId = phaseId;
          }),

          setExecutionError: (phaseId, error) => set((s) => {
            s.executionErrors[phaseId] = error;
          }),

          setImplementationAudit: (phaseId, audit) => set((s) => {
            s.implementationAudits[phaseId] = audit;
          }),

          // ── Abort ──────────────────────────────────────────────────

          getAbortController: () => activeAbortController,

          setAbortController: (controller) => {
            activeAbortController = controller;
          },

          abortExecution: () => {
            if (activeAbortController) {
              activeAbortController.abort();
              activeAbortController = null;
            }
            set((s) => {
              s.isExecuting = false;
              s.currentExecutingPhaseId = null;
              s.activeQualityGate = null;
              s.pipelineStage = 'failed';
              s.error = 'Execution aborted by user';
            });
          },

          // ── Final Audit ────────────────────────────────────────────

          setFinalAudit: (audit) => set((s) => { s.finalAudit = audit; }),

          // ── Quality Gate ───────────────────────────────────────────

          setActiveQualityGate: (gate) => set((s) => { s.activeQualityGate = gate; }),

          resolveQualityGate: (_action) => {
            // C-3: Guard against rapid double-clicks
            if (get().qualityGateResolving) return;
            set((s) => {
              s.qualityGateResolving = true;
              s.activeQualityGate = null;
              // The action value ('proceed'|'revise'|'skip'|'discard') is consumed
              // by the Phase 6 orchestration layer, not by the store itself.
              s.qualityGateResolving = false;
            });
          },

          // ── Error ──────────────────────────────────────────────────

          setError: (error) => set((s) => { s.error = error; }),

          // ── Plan History ────────────────────────────────────────────

          addToPlanHistory: (entry) => set((s) => {
            s.planHistory = [entry, ...s.planHistory].slice(0, 20);
          }),

          // ── Saved Roadmaps ──────────────────────────────────────────

          saveCurrentRoadmap: async () => {
            const { roadmap, roadmapAudit } = get();
            if (!roadmap) return;
            try {
              await planApi.saveRoadmap(roadmap, roadmapAudit ?? undefined);
              // Refresh the list
              const res = await planApi.listSavedRoadmaps();
              if (res.success) {
                set((s) => {
                  s.savedRoadmaps = res.data.roadmaps;
                  s.savedRoadmapsLoaded = true;
                });
              }
            } catch (err) {
              console.warn('[plan-store] Failed to save roadmap:', err);
            }
          },

          loadSavedRoadmaps: async () => {
            try {
              const res = await planApi.listSavedRoadmaps();
              if (res.success) {
                set((s) => {
                  s.savedRoadmaps = res.data.roadmaps;
                  s.savedRoadmapsLoaded = true;
                });
              }
            } catch (err) {
              console.warn('[plan-store] Failed to load saved roadmaps:', err);
            }
          },

          restoreRoadmap: async (id: string) => {
            try {
              const res = await planApi.getSavedRoadmap(id);
              if (res.success) {
                set((s) => {
                  s.roadmap = res.data.roadmap;
                  s.roadmapAudit = res.data.auditResult ?? null;
                  s.pipelineStage = 'reviewing';
                  s.phases = [];
                  s.phaseAudits = {};
                  s.editedPhasePrompts = {};
                  s.isActive = true;
                  s.error = null;
                });
              }
            } catch (err) {
              console.warn('[plan-store] Failed to restore roadmap:', err);
            }
          },

          deleteSavedRoadmap: async (id: string) => {
            try {
              await planApi.deleteSavedRoadmap(id);
              set((s) => {
                s.savedRoadmaps = s.savedRoadmaps.filter((r) => r.id !== id);
              });
            } catch (err) {
              console.warn('[plan-store] Failed to delete roadmap:', err);
            }
          },

          // ── Execution Metrics ──────────────────────────────────────

          setArtifactCounts: (counts) => set((s) => { s.artifactCounts = counts; }),
          incrementLLMCalls: (count = 1) => set((s) => { s.totalLLMCalls += count; }),
          setExecutionTimeMs: (ms) => set((s) => { s.executionTimeMs = ms; }),
          undoAllPhases: () => {
            // UX-4 stub: Full implementation deferred.
            console.warn('[plan-store] undoAllPhases() is a stub — full implementation deferred');
          },

          // ── Skill Wizard ──────────────────────────────────────────

          setProposedSkill: (skill) => set((s) => { s.proposedSkill = skill; }),
          setSkillReady: (ready) => set((s) => { s.skillReady = ready; }),
          clearSkillWizard: () => set((s) => {
            s.proposedSkill = null;
            s.skillReady = false;
          }),

          // ── Creative Studio Wizards ─────────────────────────────────
          setProposedAgent: (agent) => set((s) => { s.proposedAgent = agent; }),
          setAgentReady: (ready) => set((s) => { s.agentReady = ready; }),
          setProposedKnowledgeBase: (kb) => set((s) => { s.proposedKnowledgeBase = kb; }),
          setKnowledgeReady: (ready) => set((s) => { s.knowledgeReady = ready; }),
          setProposedWorkflow: (wf) => set((s) => { s.proposedWorkflow = wf; }),
          setWorkflowReady: (ready) => set((s) => { s.workflowReady = ready; }),
          setProposedSchema: (schema) => set((s) => { s.proposedSchema = schema; }),
          setDataReady: (ready) => set((s) => { s.dataReady = ready; }),
          setProposedPageDesign: (design) => set((s) => { s.proposedPageDesign = design; }),
          setPageDesignReady: (ready) => set((s) => { s.pageDesignReady = ready; }),

          // ── Timing ─────────────────────────────────────────────────

          setPipelineStartedAt: (ts) => set((s) => { s.pipelineStartedAt = ts; }),

          recordStageStart: (stage) => set((s) => {
            s.stageTimings[stage] = { startedAt: Date.now(), endedAt: null };
          }),

          recordStageEnd: (stage) => set((s) => {
            if (s.stageTimings[stage]) {
              s.stageTimings[stage].endedAt = Date.now();
            }
          }),

          recordSubStepStart: (phaseId, stepName) => set((s) => {
            s.subStepTimings[`${phaseId}:${stepName}`] = { startedAt: Date.now(), endedAt: null };
          }),

          recordSubStepEnd: (phaseId, stepName) => set((s) => {
            const key = `${phaseId}:${stepName}`;
            if (s.subStepTimings[key]) {
              s.subStepTimings[key].endedAt = Date.now();
            }
          }),

          // ── Reset ──────────────────────────────────────────────────

          reset: () => {
            if (activeAbortController) {
              activeAbortController.abort();
              activeAbortController = null;
            }
            get().deactivate();
            set(() => ({ ...INITIAL_STATE }));
          },
        }),
        {
          name: 'terramatrix:plan-store',
          storage: createJSONStorage(() => sessionStorage),

          partialize: (state) => {
            const persisted = {
              isActive: state.isActive,
              pipelineMode: state.pipelineMode,
              pipelineStage: state.pipelineStage,
              pipelineProgress: state.pipelineProgress,
              planName: state.planName,
              conversationHistory: state.conversationHistory,
              currentThreadId: state.currentThreadId,
              plan: state.plan,
              planReady: state.planReady,
              researchContext: state.researchContext,
              roadmap: state.roadmap,
              roadmapAudit: state.roadmapAudit,
              phases: state.phases,
              phaseAudits: state.phaseAudits,
              editedPhasePrompts: state.editedPhasePrompts,
              discoveredSchemas: state.discoveredSchemas,
              planHistory: state.planHistory,
              // Excluded (transient): isExecuting, currentExecutingPhaseId, executionErrors,
              // implementationAudits, activeQualityGate, finalAudit, error,
              // all loading flags (isConversingLoading, isResearching, etc.),
              // qualityGateResolving, researchProgress
            };

            // GAP-6: sessionStorage size management — trim if >2MB
            const serialized = JSON.stringify(persisted);
            if (serialized.length > 2_000_000) {
              if (persisted.researchContext?.findings) {
                persisted.researchContext = {
                  ...persisted.researchContext,
                  findings: persisted.researchContext.findings.map((f) => ({
                    ...f,
                    rawContentPreview: f.rawContentPreview.slice(0, 100) + '...',
                  })),
                };
              }
              if (persisted.conversationHistory?.length > 10) {
                persisted.conversationHistory = persisted.conversationHistory.slice(-10);
              }
              console.warn('[plan-store] Persisted state trimmed due to size limit');
            }

            return persisted;
          },

          onRehydrateStorage: () => (state, error) => {
            if (error) {
              console.warn('[plan-store] Failed to rehydrate:', error);
              return;
            }
            if (!state) return;

            // Normalize plan.sections if stale Record shape from sessionStorage
            if (state.plan?.sections && !Array.isArray(state.plan.sections)) {
              state.plan.sections = Object.entries(state.plan.sections as Record<string, unknown>).map(
                ([key, section]) => {
                  const s = section as Record<string, unknown>;
                  let domainKey = key;
                  let pageName: string | undefined;
                  if (key.startsWith('visual-editor:')) {
                    domainKey = 'visual-editor';
                    pageName = key.split(':')[1];
                  }
                  return {
                    id: `section-${key}`,
                    domain: domainKey as Plan['sections'][number]['domain'],
                    title: (s.title || key) as string,
                    description: (s.description || '') as string,
                    executionPrompt: (s.executionPrompt || '') as string,
                    details: (s.details || {}) as PlanSectionDetails,
                    dependsOn: ((s.dependsOn || []) as string[]).map((dep: string) =>
                      dep.startsWith('section-') ? dep : `section-${dep}`),
                    status: (s.status || 'pending') as PlanSectionStatus,
                    ...(pageName ? { pageName } : {}),
                  };
                },
              );
            }

            // G-4: Detect stale execution state from crashed session
            const executionStages: PlanPipelineStage[] = [
              'executing_phase', 'auditing_implementation', 'final_audit',
            ];
            if (executionStages.includes(state.pipelineStage)) {
              // Crashed mid-execution — reset to reviewing so user can resume
              state.pipelineStage = 'reviewing';
            }

            // Re-establish lock heartbeat if active
            if (state.isActive) {
              localStorage.setItem(
                LOCK_KEY,
                JSON.stringify({ tabId: TAB_ID, timestamp: Date.now() }),
              );
              if (heartbeatInterval) clearInterval(heartbeatInterval);
              heartbeatInterval = setInterval(() => {
                localStorage.setItem(
                  LOCK_KEY,
                  JSON.stringify({ tabId: TAB_ID, timestamp: Date.now() }),
                );
              }, LOCK_HEARTBEAT_MS);
            }
          },
        },
      ),
    ),
  ),
);

// ---------------------------------------------------------------------------
// Derived selectors
// ---------------------------------------------------------------------------

export const useIsFullMode = () =>
  usePlanStore((s) => s.pipelineMode === 'full');

export const useIsAtCheckpoint = () =>
  usePlanStore((s) => s.pipelineStage === 'reviewing');

export const usePipelineStageLabel = () =>
  usePlanStore((s) => PIPELINE_STAGE_LABELS[s.pipelineStage]);
